import React, { ReactNode } from "react";
import i18n, { Resource, i18n as I18nInstance } from "i18next";
import { I18nextProvider, initReactI18next } from "react-i18next";

import { config } from "./config";
import { getResourcesByBuildType, ResourceLoader } from "./ssr-resources";
import { formatLocale } from "./utils";
import { getNamespaceForBuildType } from "./resources";

let baseI18nInstance: I18nInstance | null = null;
const localeInstances = new Map<string, I18nInstance>();

export const configureSsrI18n = async (resources: Resource, lng: string = "en"): Promise<I18nInstance> => {
  const instance = i18n.createInstance();
  await instance.use(initReactI18next).init(
    config(lng, resources, {
      load: "all",
      saveMissing: false,
      missingKeyHandler: false,
      parseMissingKeyHandler: () => "",
    }),
  );

  return instance;
};

export const initSsrI18n = async (resourceLoader: ResourceLoader, buildType: string): Promise<I18nInstance> => {
  if (!baseI18nInstance) {
    const resources = getResourcesByBuildType(resourceLoader)(buildType);
    baseI18nInstance = await configureSsrI18n(resources, "en");
  }
  return baseI18nInstance;
};

// Get or create a cached i18n instance for a specific locale
// This ensures we use the same instance structure for SSR and hydration
const getLocaleInstance = (locale: string): I18nInstance => {
  // Use consistent formatting with browser
  const formattedLocale = formatLocale(locale);

  // Return cached instance if it exists
  if (localeInstances.has(formattedLocale)) {
    return localeInstances.get(formattedLocale)!;
  }

  // Create a new cloned instance for this locale
  const instance = baseI18nInstance
    ? baseI18nInstance.cloneInstance({ lng: formattedLocale })
    : i18n.cloneInstance({ lng: formattedLocale });

  // Cache and return
  localeInstances.set(formattedLocale, instance);
  return instance;
};

export const buildI18NextServerProvider = (resourceLoader: ResourceLoader, buildType: string) => {
  // Initialize SSR i18n synchronously - this loads all language resources
  const namespace = getNamespaceForBuildType(buildType);
  const resources = getResourcesByBuildType(resourceLoader)(buildType);

  // Create base instance synchronously
  if (!baseI18nInstance) {
    const instance = i18n.createInstance();
    instance.use(initReactI18next).init(
      config("en", resources, {
        load: "all",
        saveMissing: false,
        missingKeyHandler: false,
        parseMissingKeyHandler: () => "",
        defaultNS: namespace,
        ns: namespace,
      }),
    );
    baseI18nInstance = instance;
  }

  const I18NextServerProvider = ({ children, locale }: { children: ReactNode; locale?: string }) => {
    if (!locale) {
      throw new Error("locale prop is required for I18NextServerProvider");
    }

    const instance = getLocaleInstance(locale);

    return <I18nextProvider i18n={instance}>{children}</I18nextProvider>;
  };

  return I18NextServerProvider;
};
